/**
 * A size as the customer names it — bata-ui#196 §8, bata-platform#470 §1, `docs/adr/9027`.
 *
 * Every sized read carried `size_key` alone: `dim_size`'s 32-hex MD5 of `(size_scale,
 * size_code)`. It is an identifier, never a size a planner can read, and the hash is one-way,
 * so this front end never decodes it and never joins reads to name it. Since platform ADR 2006
 * each sized row also publishes `size_code`, `size_scale` and `size_run_position`, or
 * `size_absent_reason` when the platform could not name the key. This module is the one place
 * the product turns those fields into what a planner sees:
 *
 *   - **the code**, with its scale in front where the sizes on screen come from more than one
 *     scale (`EU 41` beside `US 8`), and alone where they do not (`41`);
 *   - **in run order** — `size_run_position`, never the code's spelling, so `XS S M L` and
 *     `EU 39 40 41` both read in the order the scale declares (G2.5);
 *   - **absent with its reason** when the platform named nothing: the em-dash with an
 *     announced label and a badge that says why (`size_dimension_not_landed`,
 *     `size_not_in_dimension`, or — on a read that does not publish the fields at all — not
 *     published). Never the key, and never a guess.
 *
 * Promoted rather than written per chapter because five chapters render sizes (G11: "shared
 * primitives live in one place and are imported, not copied").
 */

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { useSizeScaleLabel } from '../api';
import type { ExportCell, ExportColumn } from '../export';
import { Badge } from '../primitives';

/** The platform's snake_case spelling of a sized row (ADR 2006). */
export interface SizeFields {
  readonly size_key: string;
  readonly size_code?: string | null;
  readonly size_scale?: string | null;
  readonly size_run_position?: number | null;
  readonly size_absent_reason?: string | null;
}

/** The camelCase spelling some reads serialise (`SelectionSampleLine`, `DemandPanelCell`). */
export interface AliasedSizeFields {
  readonly sizeKey: string;
  readonly sizeCode?: string | null;
  readonly sizeScale?: string | null;
  readonly sizeRunPosition?: number | null;
  readonly sizeAbsentReason?: string | null;
}

/** One size, read once from its row. `code === null` is an absence and `absentReason` says why. */
export interface SizeName {
  /** The opaque key: an identifier for joins and exports, never shown as the size. */
  readonly key: string;
  readonly code: string | null;
  readonly scale: string | null;
  readonly position: number | null;
  /** The platform's code, or `null` where the read did not publish one. */
  readonly absentReason: string | null;
}

/** The platform's absence codes (ADR 2006), and the catalogue key each renders as. */
export const SIZE_ABSENT_REASON_KEYS: Readonly<Record<string, string>> = {
  size_dimension_not_landed: 'size.absent.notLanded',
  size_not_in_dimension: 'size.absent.notInDimension',
  // POST /launch/allocate (bata-platform ADR 2006 as amended): the caller's grant does not cover
  // the launch lines' read the names come from, so the allocation answers without them.
  size_name_not_authorised: 'size.absent.notAuthorised',
  // An option-level row (an exit consolidation lane, the allocation store total) is not at size
  // grain, so there is no size to name: bata-platform#771, ADR 2006 amended 2026-10-19.
  size_all_sizes: 'size.absent.allSizes',
};

/**
 * The reason that is not an absence at all: the row covers every size of its option. It reads as
 * «All sizes» on its own, never as «Size not named» with a badge, which would call it a defect.
 */
export const SIZE_ALL_SIZES = 'size_all_sizes';

/** A read that publishes no name and no reason: an older contract, or a field not yet added. */
export const SIZE_NOT_PUBLISHED_KEY = 'size.absent.notPublished';

function present(value: string | null | undefined): string | null {
  return value === null || value === undefined || value.trim() === '' ? null : value;
}

export function sizeName(row: SizeFields): SizeName {
  const code = present(row.size_code);
  return {
    key: row.size_key,
    code,
    scale: code === null ? null : present(row.size_scale),
    position: code === null ? null : (row.size_run_position ?? null),
    absentReason: code === null ? (row.size_absent_reason ?? null) : null,
  };
}

export function sizeNameAliased(row: AliasedSizeFields): SizeName {
  return sizeName({
    size_key: row.sizeKey,
    size_code: row.sizeCode ?? null,
    size_scale: row.sizeScale ?? null,
    size_run_position: row.sizeRunPosition ?? null,
    size_absent_reason: row.sizeAbsentReason ?? null,
  });
}

/** The catalogue key naming why `name` has no code; `null` when it has one. */
export function sizeAbsentReasonKey(name: SizeName): string | null {
  if (name.code !== null) return null;
  return (name.absentReason === null ? undefined : SIZE_ABSENT_REASON_KEYS[name.absentReason])
    ?? SIZE_NOT_PUBLISHED_KEY;
}

/**
 * Run order: grouped by scale where two sizes are of different scales, then the scale's declared
 * position, then code. An unnamed size has no position and sorts after every named one, by key so
 * the order is stable.
 *
 * **The scale first, when scales mix** (design/09 §12.11, `docs/adr/9034`). A position is a place
 * in ITS scale's run, so comparing positions across scales interleaved them — `EU 39 · US 6 · EU 40
 * · US 7` — on the stock map's axis, which reads as one run when it is two. Within one scale the
 * comparison is unchanged.
 */
export function compareSizeNames(a: SizeName, b: SizeName): number {
  if (a.scale !== null && b.scale !== null && a.scale !== b.scale) return a.scale.localeCompare(b.scale);
  if (a.position !== null && b.position !== null && a.position !== b.position) {
    return a.position - b.position;
  }
  if ((a.position === null) !== (b.position === null)) return a.position === null ? 1 : -1;
  const scale = (a.scale ?? '').localeCompare(b.scale ?? '');
  if (scale !== 0) return scale;
  const code = (a.code ?? '').localeCompare(b.code ?? '', undefined, { numeric: true });
  return code !== 0 ? code : a.key.localeCompare(b.key);
}

/** `rows` in run order of the size each carries; equal sizes keep their order. */
export function sortBySize<T>(rows: readonly T[], of: (row: T) => SizeName): T[] {
  return rows
    .map((row, index) => ({ row, index, name: of(row) }))
    .sort((a, b) => compareSizeNames(a.name, b.name) || a.index - b.index)
    .map((entry) => entry.row);
}

/** The distinct sizes among `names`, one per key, in run order — a chart's or a map's axis. */
export function sizeAxis(names: Iterable<SizeName>): readonly SizeName[] {
  const byKey = new Map<string, SizeName>();
  for (const name of names) if (!byKey.has(name.key)) byKey.set(name.key, name);
  return [...byKey.values()].sort(compareSizeNames);
}

/** Whether the named sizes come from more than one scale — then the scale is shown too. */
export function scalesMix(names: Iterable<SizeName>): boolean {
  const scales = new Set<string>();
  for (const name of names) if (name.scale !== null) scales.add(name.scale);
  return scales.size > 1;
}

/**
 * A size as words: the code, `scale code` where scales mix, or the absence's own label. For an
 * `aria-label`, a title, a joined reference and a table's sort-and-filter value — anywhere a
 * string is wanted. A table's filter value is {@link sizeFilterValue}, which keeps an unnamed
 * size absent rather than spelling it.
 *
 * **The scale is said in the platform's words** (`ADULT_SHOE` → «Calzature adulto», vocabulary
 * `size_scale`, ADR 2125): every size on every axis, tooltip, aria-label and cell is spelt here,
 * so this is the one place the scale's label is joined. The code stands for a scale the platform
 * has no label for, exactly as before. The size's own code (`35`) is a size, not a vocabulary term.
 */
export function useSizeText(): (name: SizeName, withScale: boolean) => string {
  const { t } = useTranslation();
  const scaleLabel = useSizeScaleLabel();
  return useCallback(
    (name: SizeName, withScale: boolean): string => {
      if (name.code === null) {
        return t(name.absentReason === SIZE_ALL_SIZES ? 'size.absent.allSizes' : 'size.absent.label');
      }
      return withScale && name.scale !== null
        ? t('size.withScale', { scale: scaleLabel(name.scale), code: name.code })
        : name.code;
    },
    [t, scaleLabel],
  );
}

/** The value a table column sorts and filters a size on: its words, or absent. */
export function sizeFilterValue(name: SizeName, withScale: boolean): string | null {
  if (name.code === null) return null;
  return withScale && name.scale !== null ? [name.scale, name.code].join('\u00a0') : name.code;
}

/** What a table column sorts a size on: its position in the run. Absent sorts last. */
export function sizeSortValue(name: SizeName): number | null {
  return name.position;
}

export interface SizeLabelProps {
  readonly name: SizeName;
  /** Show the scale before the code — pass `scalesMix(...)` over the sizes on screen. */
  readonly withScale?: boolean;
}

/**
 * A size inline: the code (and scale), or the designed absent state — «Size not named» in words
 * (ADR 9061) with a badge saying why. Never the key.
 */
export function SizeLabel({ name, withScale = false }: SizeLabelProps): React.JSX.Element {
  const { t } = useTranslation();
  const text = useSizeText();
  const reasonKey = sizeAbsentReasonKey(name);
  if (reasonKey === null) return <span className="font-numeric">{text(name, withScale)}</span>;
  if (name.absentReason === SIZE_ALL_SIZES) return <span>{t('size.absent.allSizes')}</span>;
  return (
    <span className="inline-flex items-baseline gap-1 text-ink-secondary">
      <span>{t('size.absent.label')}</span>
      <Badge labelKey={reasonKey} />
    </span>
  );
}

export interface SizeCellProps extends SizeLabelProps {
  readonly className?: string;
  /** `th` for a table whose rows are sizes (the size is the row's header). */
  readonly as?: 'td' | 'th';
}

/** A size as a table cell, the `<td>` a `DataColumn`'s `cell` returns. */
export function SizeCell({ name, withScale = false, className, as = 'td' }: SizeCellProps): React.JSX.Element {
  const label = <SizeLabel name={name} withScale={withScale} />;
  if (as === 'th') {
    return <th scope="row" className={['text-start font-normal', className].filter(Boolean).join(' ')}>{label}</th>;
  }
  return <td {...(className === undefined ? {} : { className })}>{label}</td>;
}

/**
 * The five columns a sized export row carries, in place of the key alone: the key stays as an
 * identifier, and the size is written as the platform named it — or `missing` with the
 * platform's reason code beside it.
 */
export const SIZE_EXPORT_COLUMNS: readonly ExportColumn[] = [
  { key: 'size_key', headerKey: 'size.column.key', kind: 'text', descriptionKey: 'size.columnGuide.size_key', technical: true },
  { key: 'size_code', headerKey: 'size.column.code', kind: 'text', descriptionKey: 'size.columnGuide.size_code' },
  { key: 'size_scale', headerKey: 'size.column.scale', kind: 'text', descriptionKey: 'size.columnGuide.size_scale', technical: true },
  { key: 'size_run_position', headerKey: 'size.column.runPosition', kind: 'integer', descriptionKey: 'size.columnGuide.size_run_position', technical: true },
  { key: 'size_absent_reason', headerKey: 'size.column.absentReason', kind: 'text', descriptionKey: 'size.columnGuide.size_absent_reason' },
];

/** The keys of {@link SIZE_EXPORT_COLUMNS}, for a request's `visibleColumns`. */
export const SIZE_EXPORT_KEYS: readonly string[] = SIZE_EXPORT_COLUMNS.map((column) => column.key);

const NOT_REPORTED: ExportCell = { kind: 'missing', state: 'not-reported' };

/** One row's cells for {@link SIZE_EXPORT_COLUMNS}. */
export function sizeExportCells(name: SizeName): readonly ExportCell[] {
  return [
    { kind: 'text', value: name.key },
    name.code === null ? NOT_REPORTED : { kind: 'text', value: name.code },
    name.scale === null ? NOT_REPORTED : { kind: 'text', value: name.scale },
    name.position === null ? NOT_REPORTED : { kind: 'integer', raw: name.position },
    name.code !== null
      ? NOT_REPORTED
      : { kind: 'text', value: name.absentReason ?? 'size_not_published' },
  ];
}
